import React from "react";
import {
  ActionButton,
  Callout,
  classNamesFunction,
  DirectionalHint,
  IButtonStyles,
  IconButton,
  INavLink,
  INavLinkGroup,
  IStyle,
  mergeStyleSets,
  Stack,
  styled,
} from "@fluentui/react";
import { useTheme } from "cayo.ui";
import styledComponent from "styled-components";
import { IElementStyles } from "../../api/schema.api";
import logger from "@cayo/shared/libs/logger";
import Icon from "../Graphics/Icon";

type ItemOrChildSelected = {
  selected: boolean;
  childSelected: boolean;
  menuVisible?: boolean;
};
type LoadSubnodesFn = (ev?: React.MouseEvent<HTMLElement>, link?: INavLink) => Promise<void>;
interface INavCollapsedProps {
  groups?: INavLinkGroup[];
  selectedKey?: string;
  styles?: INavCollapsedStyles;
  expandLink: LoadSubnodesFn;
}
interface INavCollapsedStyleProps {
  color: string;
  backgroundColor: string;
  hoverBg: string;
}
interface INavCollapsedStyles {
  root?: IStyle;
  menuHeader?: IStyle;
}

function defaultNavCollapsedStyles(props: INavCollapsedStyleProps): INavCollapsedStyles {
  return {
    root: [
      {
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      },
      props.backgroundColor && {
        backgroundColor: props.backgroundColor,
      },
    ],
    menuHeader: [
      {
        padding: "12px 10px",
        height: 44,
        display: "flex",
        alignItems: "center",
      },
      props.backgroundColor && {
        backgroundColor: props.hoverBg,
      },
    ],
  };
}

const getClassNames = classNamesFunction<INavCollapsedStyleProps, INavCollapsedStyles>();

const log = logger.getLogger("NavCollapsed");

type NavLinkData = {
  link: INavLink;
  level: number;
  selected: boolean;
};

function makeLinkData(
  links: INavLink[],
  selectedKey: string,
  loadSubNodes: LoadSubnodesFn
): NavLinkData[] {
  const MAX_DEEP = 4;

  function needLoadSubnodes(link: INavLink) {
    return link.links?.length === 1 && link.links[0].key?.endsWith("loading-link");
  }

  function mapLinks(links: INavLink[], level: number): NavLinkData[] {
    log.debug("makeLinkData.mapLinks -> links, level", links, level);
    const result = links.reduce((acc, l) => {
      acc.push({
        link: l,
        level: level,
        selected: l.key === selectedKey,
      });
      // call load subnodes if need
      if (needLoadSubnodes(l)) {
        log.debug("makeLinkData.mapLinks -> need load subnodes", l);
        loadSubNodes(undefined, l);
      }
      if (!!l.links && level <= MAX_DEEP) {
        acc = acc.concat(mapLinks(l.links, level + 1));
      }
      return acc;
    }, [] as NavLinkData[]);
    return result;
  }

  if (!links) {
    return [];
  }
  const result = mapLinks(links, 0);
  log.debug("makeLinkData -> result", result);
  return result;
}

const menuWidth = 270;
const levelPadding = 22;
const labelStartWidth = menuWidth - 50;

const NavCollapsed = (props: INavCollapsedProps) => {
  log.debug("render -> props", props);
  const { groups, selectedKey } = props;
  const theme = useTheme();

  const {
    background,
    "item-text-color": itemTextColor,
    "hover-item-background": hoverItemBackground,
    "selected-item-text-color": selectedItemTextColor,
  } = (theme.elementStyles as unknown as IElementStyles).navBar || {};

  const colors = {
    color: itemTextColor || "#000", // fallback to black if undefined
    background: background || theme.cayoTheme.brandColors.primaryBackground,
    highlite: hoverItemBackground,
    hoverBg: hoverItemBackground || selectedItemTextColor || "#e5e5e5", // fallback to light gray if undefined
    selectedColor: selectedItemTextColor || "#000", // fallback to black if undefined
  };

  function checkSelected(key: string): ItemOrChildSelected {
    return {
      selected: key === selectedKey,
      childSelected: (key !== selectedKey && selectedKey?.startsWith(key)) || false,
    };
  }

  const classes = getClassNames(props.styles, {
    color: colors.color,
    backgroundColor: colors.background,
    hoverBg: colors.hoverBg,
  });

  const links = !groups || groups.length === 0 ? [] : groups[0].links;
  const rootButtonStyles: IButtonStyles = {
    root: {
      backgroundColor: colors.background,
      height: 44,
      width: "100%",
      borderRadius: 0,
    },
    rootHovered: {
      backgroundColor: colors.hoverBg,
    },
    icon: {
      color: colors.color,
    },
  };

  function selectedRootButtonStyle(args: ItemOrChildSelected): IButtonStyles {
    return {
      root: [
        (args.selected || args.childSelected) &&
          args.menuVisible === false && {
            backgroundColor: colors.hoverBg,
            borderLeft: `3px solid ${colors.selectedColor}`,
          },
      ],
      rootHovered: [
        args.childSelected && {
          backgroundColor: colors.hoverBg,
        },
        args.selected && {
          backgroundColor: colors.hoverBg,
        },
      ],
    };
  }

  const menuButtonStyle: IButtonStyles = {
    root: {
      backgroundColor: colors.background,
      color: colors.color,
      borderRadius: 0,
      height: 50,
    },
    rootHovered: {
      backgroundColor: colors.hoverBg,
      color: colors.color,
    },
    icon: {
      color: colors.color,
    },
    iconHovered: {
      color: colors.color,
    },
    label: {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      textAlign: "left",
      lineHeight: 16,
    },
  };
  function selectedMenuButtonStyle(isSelected: boolean, level: number): IButtonStyles {
    const padding = level * levelPadding;
    return {
      root: [
        isSelected && {
          backgroundColor: colors.hoverBg,
        },
        {
          paddingLeft: padding,
        },
      ],
      rootHovered: [
        isSelected && {
          backgroundColor: colors.hoverBg,
        },
      ],

      iconHovered: {
        color: isSelected ? colors.selectedColor : colors.color,
      },

      icon: {
        color: isSelected ? colors.selectedColor : colors.color,
      },
      label: {
        color: isSelected ? colors.selectedColor : colors.color,
        width: labelStartWidth - padding,
      },
    };
  }

  // TODO: make item component and use it
  const renderItem = (link: INavLink) => {
    const [showSubmenu, SetShowSubmenu] = React.useState(false);
    const btnRef = React.useRef(null);

    const hasChildLinks = !!link.links && link.links?.length !== 0;
    const linkSelected = {
      ...checkSelected(link.key ?? ""),
      menuVisible: hasChildLinks && showSubmenu,
    };

    log.debug(
      "root -> key, selectedKey, selected, childSelected",
      link.key,
      selectedKey,
      linkSelected
    );
    const btnStyle = mergeStyleSets(rootButtonStyles, selectedRootButtonStyle(linkSelected));

    return (
      <div
        ref={btnRef}
        key={link.key}
        onMouseEnter={() => SetShowSubmenu(true)}
        onMouseLeave={() => SetShowSubmenu(false)}
      >
        <IconButton
          key={link.key}
          title={link.name}
          iconProps={{ iconName: link.icon }}
          href={link.url}
          styles={btnStyle}
        />
        {hasChildLinks && showSubmenu && (
          <Callout
            target={btnRef}
            isBeakVisible={false}
            directionalHint={DirectionalHint.rightTopEdge}
            calloutWidth={menuWidth}
            layerProps={{ styles: { root: { zIndex: 1000002 } } }} //loading overlay
          >
            <Stack>
              <div className={classes.menuHeader}>{link.name}</div>
              {makeLinkData(link.links ?? [], selectedKey ?? "", props.expandLink).map((ld) => (
                <ActionButton
                  key={ld.link.name}
                  iconProps={{ iconName: ld.link.icon }}
                  onRenderIcon={(p, r) => {
                    if (p?.iconProps?.iconName && p.iconProps.iconName.indexOf("+") > 0) {
                      return (
                        <>
                          <Icon
                            iconName={p.iconProps.iconName}
                            // eslint-disable-next-line no-inline-styles/no-inline-styles
                            style={{ marginLeft: 4 }}
                          />
                          <SmallSpan />
                        </>
                      );
                    }

                    return r!(p);
                  }}
                  href={ld.link.url}
                  text={ld.link.name}
                  title={ld.link.name}
                  styles={mergeStyleSets(
                    menuButtonStyle,
                    selectedMenuButtonStyle(ld.selected, ld.level)
                  )}
                />
              ))}
            </Stack>
          </Callout>
        )}
      </div>
    );
  };
  return <div className={classes.root}>{links.map((l) => renderItem(l))}</div>;
};

const SmallSpan = styledComponent.span`
  width: 2px;
`;

export default styled<INavCollapsedProps, INavCollapsedStyleProps, INavCollapsedStyles>(
  NavCollapsed,
  defaultNavCollapsedStyles
);
